import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { Box } from '@hopskipdrive/react-components';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import { Button } from '@rideiq/shared/components/Button';

import { EditLocationModal } from '../../../RiderLocations/EditLocationModal';
import { RiderLocation } from '../../../RiderLocations/Location/Location.types';
import { RiderLocationFormData } from '../../../RiderLocations/RiderLocationForm/RiderLocationForm.types';
import { LocationDraft, RiderFormValues } from '../../types';

import { AddLocationModal } from './AddLocationModal';
import { LocationCard } from './LocationCard';
import { getLocationCardLabel, isHiddenByImmediateReplacement } from './locationReplacementDisplay';
import { DashedAddContainer } from './styles';

const toLocationProtocol = (
  protocol: { value: string; label: string } | string | undefined
): { value: string; label: string } | undefined => {
  if (!protocol) {
    return undefined;
  }
  if (typeof protocol === 'string') {
    return { value: protocol, label: '' };
  }

  return protocol;
};

const toNumericLocationId = (id: number | string): number => Number(id);

export const LocationsSection = () => {
  const { watch, setValue } = useFormContext<RiderFormValues>();
  const watchLocationDrafts = watch('locationDrafts') ?? [];
  const { slug } = useParams<{ slug?: string }>();
  const [modalState, setModalState] = useState<null | 'add' | string>(null);

  const handleAddSave = (formData: RiderLocationFormData, locationDetails: RiderLocation) => {
    const enrichedLocation: RiderLocation = {
      ...locationDetails,
      id: toNumericLocationId(locationDetails.id),
      primary: formData.primary ?? false,
      mustBeMet: formData.mustBeMet,
      authorizedContactGroups: formData.mustBeMetProtocol?.authorizedContactGroups,
      locationProtocol: toLocationProtocol(formData.mustBeMetProtocol?.locationProtocol),
      replaceLocation: formData.replaceLocation,
    };
    const newDraft: LocationDraft = {
      draftId: crypto.randomUUID(),
      location: enrichedLocation,
      formData,
      isNew: true,
    };

    setValue('locationDrafts', [...watchLocationDrafts, newDraft], { shouldDirty: true });
    setModalState(null);
  };

  const handleEditSave = (formData: RiderLocationFormData, locationDetails: RiderLocation) => {
    const enrichedLocation: RiderLocation = {
      ...locationDetails,
      id: toNumericLocationId(locationDetails.id),
      primary: formData.primary ?? false,
      mustBeMet: formData.mustBeMet,
      authorizedContactGroups: formData.mustBeMetProtocol?.authorizedContactGroups,
      locationProtocol:
        toLocationProtocol(formData.mustBeMetProtocol?.locationProtocol) ?? locationDetails.locationProtocol,
      replaceLocation: formData.replaceLocation,
    };

    const previousFormData = watchLocationDrafts.find((d) => d.draftId === modalState)?.formData;
    const changedFields = (Object.keys(formData) as Array<keyof RiderLocationFormData>).filter(
      (key) => JSON.stringify(formData[key]) !== JSON.stringify(previousFormData?.[key])
    );

    setValue(
      'locationDrafts',
      watchLocationDrafts.map((d) =>
        d.draftId === modalState ? { ...d, location: enrichedLocation, formData, isDirty: true, changedFields } : d
      ),
      { shouldDirty: true }
    );
    setModalState(null);
  };

  const handleRemove = (draftId: string) => {
    setValue(
      'locationDrafts',
      watchLocationDrafts.filter((d) => d.draftId !== draftId),
      { shouldDirty: true }
    );
    setModalState(null);
  };

  const editTarget =
    typeof modalState === 'string' && modalState !== 'add'
      ? watchLocationDrafts.find((d) => d.draftId === modalState)
      : null;

  const visibleDrafts = watchLocationDrafts.filter(
    (draft) => !isHiddenByImmediateReplacement(draft, watchLocationDrafts)
  );

  return (
    <>
      <Box display="flex" flexDirection="column" gap="12px">
        {visibleDrafts.map((draft) => (
          <LocationCard
            key={draft.draftId}
            draft={draft}
            label={getLocationCardLabel(draft, watchLocationDrafts)}
            onEdit={() => setModalState(draft.draftId)}
          />
        ))}
        <DashedAddContainer>
          <Button
            color="tertiary"
            size="medium"
            onClick={() => setModalState('add')}
            data-testid="add-location"
            aria-label="Add location"
            style={{
              fontWeight: '400',
            }}
          >
            <AddRoundedIcon width="9px" height="9px" sx={{ marginRight: '8px' }} />
            Add location
          </Button>
        </DashedAddContainer>
      </Box>

      {modalState === 'add' && (
        <AddLocationModal
          isOpen={true}
          associatedLocations={watchLocationDrafts.map((d) => d.location)}
          riderSlug={slug ?? ''}
          onSave={handleAddSave}
          onCancel={() => setModalState(null)}
        />
      )}

      {editTarget && (
        <EditLocationModal
          key={editTarget.draftId}
          isOpen={true}
          location={editTarget.location}
          isNew={editTarget.isNew}
          isDirty={editTarget.isDirty}
          associatedLocations={watchLocationDrafts.map((d) => d.location)}
          riderSlug={slug ?? ''}
          isLoading={false}
          onSubmit={handleEditSave}
          onDelete={() => handleRemove(editTarget.draftId)}
          onCancel={() => setModalState(null)}
          hideSavedLocationsInFindAndReplace
        />
      )}
    </>
  );
};
